import { normalizeMatchRoomCode } from "../../utils/matchRoomCode";
import { fetchSessionMatch, getRoomMeta, verifyRoomJoin } from "../sessionService";
import { getCurrentAuthUser } from "../../repositories/authRepository";
import * as arenaRepository from "../../repositories/arenaRepository";
import { canEditCompetition } from "../../permissions/competitionPermissions";
import { isFirestoreInternalError } from "../../utils/firestorePersistenceRecovery";

async function canResumeAsTournamentOrganizer(match, uid) {
  const arenaId = match?.arenaId || match?.activeCompetitionId;
  if (!uid || !arenaId) return false;
  try {
    const arena = await arenaRepository.fetchCompetition(arenaId);
    return Boolean(arena && canEditCompetition(arena, uid));
  } catch {
    return false;
  }
}

/**
 * @param {string} roomCodeInput
 * @param {{
 *   creatorPlaysInSession?: boolean;
 * }} [hints]
 */
export async function resumeRoomSession(roomCodeInput, hints = {}) {
  const room = normalizeMatchRoomCode(roomCodeInput);
  if (!room) {
    return { ok: false, reason: "invalid" };
  }

  const uid = getCurrentAuthUser()?.uid;

  let match;
  try {
    match = await fetchSessionMatch(room);
  } catch (err) {
    if (isFirestoreInternalError(err)) {
      return { ok: false, reason: "firestore_error", error: err };
    }
    return { ok: false, reason: "not_found" };
  }
  if (!match) {
    return { ok: false, reason: "not_found" };
  }
  if (match.archivedAt) {
    return { ok: false, reason: "archived" };
  }

  let meta;
  try {
    meta = await getRoomMeta(room);
  } catch (err) {
    if (isFirestoreInternalError(err)) {
      return { ok: false, reason: "firestore_error", error: err };
    }
    return { ok: false, reason: "not_found" };
  }
  if (!meta) {
    return { ok: false, reason: "not_found" };
  }

  const roomDisplayName = meta.name || match.displayName || room;
  const isCreator = Boolean(uid && match.createdBy === uid);
  const isDeputy = Boolean(uid && match.deputyRefereeUid === uid);

  if (isCreator) {
    return {
      ok: true,
      room,
      role: "referee",
      roomDisplayName,
      isCreator: true,
      registerAsCreator: true,
      creatorPlaysInSession: hints.creatorPlaysInSession !== false,
    };
  }

  if (isDeputy) {
    return {
      ok: true,
      room,
      role: "referee",
      roomDisplayName,
      isCreator: false,
      isDeputy: true,
      registerAsCreator: false,
      creatorPlaysInSession: false,
    };
  }

  if (await canResumeAsTournamentOrganizer(match, uid)) {
    return {
      ok: true,
      room,
      role: "referee",
      roomDisplayName,
      isCreator: match.createdBy === uid,
      registerAsCreator: match.createdBy === uid,
      creatorPlaysInSession: false,
    };
  }

  const join = await verifyRoomJoin(room, "");
  if (!join.ok) {
    return { ok: false, reason: join.reason || "denied" };
  }

  const role = join.role === "referee" ? "referee" : "spectator";

  return {
    ok: true,
    room,
    role,
    roomDisplayName,
    isCreator: false,
    registerAsCreator: false,
    creatorPlaysInSession: false,
  };
}

/** Room still exists and is not soft-archived. */
export async function isSessionRoomActive(roomCodeInput) {
  const room = normalizeMatchRoomCode(roomCodeInput);
  if (!room) return false;
  try {
    const match = await fetchSessionMatch(room);
    return Boolean(match && !match.archivedAt);
  } catch {
    return false;
  }
}
